// Shared layout + helpers for the candidate / external pages that open from an emailed
// token link without logging in (/offer/:token, /jd-resume-upload/:token,
// /jd-resume-review/:token, /candidate-form/:token).
import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import recruiterTheme, {
  COLORS, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING,
} from "../../styles/recruiterTheme";
import logo from "../../styles/logo.png";

export const MAX_UPLOAD_MB = 10;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Returns an error message for a picked file, or "" when it is acceptable.
// `exts` is a list like [".pdf", ".png"].
export const validateFile = (file, exts) => {
  if (!file) return "No file selected.";
  const name = (file.name || "").toLowerCase();
  if (exts?.length && !exts.some((e) => name.endsWith(e))) {
    return `"${file.name}" is not a supported file type. Allowed: ${exts.map((e) => e.slice(1).toUpperCase()).join(", ")}.`;
  }
  if (file.size === 0) return `"${file.name}" is empty.`;
  if (file.size > MAX_UPLOAD_BYTES) {
    return `"${file.name}" is ${formatSize(file.size)}. The maximum size is ${MAX_UPLOAD_MB} MB.`;
  }
  return "";
};

// Parses a fetch Response as JSON without throwing a cryptic "Unexpected token <" when the
// server (or a proxy) answers with HTML — e.g. 413 Request Entity Too Large or a 502.
export const readJson = async (res) => {
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON body */ }
  if (data && typeof data === "object") return data;
  if (res.status === 413) return { success: false, message: "The upload is too large. Please use smaller files." };
  return { success: false, message: `The server could not process the request (HTTP ${res.status}). Please try again later.` };
};

// Turns a thrown fetch error into something a candidate can act on.
export const networkMessage = (err) =>
  err?.name === "TypeError" || /failed to fetch|network/i.test(err?.message || "")
    ? "Could not reach the server. Please check your connection and try again."
    : (err?.message || "Something went wrong. Please try again.");

function Brand({ subtitle }) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} mb={3}>
      <Box component="img" src={logo} alt="" sx={{ width: 40, height: 40, borderRadius: `${RADIUS.sm}px`, bgcolor: COLORS.surface, flexShrink: 0 }} />
      <Box minWidth={0}>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep, lineHeight: 1.2 }}>
          ZentreeLabs
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );
}

// Centred single-column page on the lavender canvas. `maxWidth` defaults to 720.
export default function PublicPageShell({ subtitle, maxWidth = 720, children }) {
  return (
    <ThemeProvider theme={recruiterTheme}>
      <Box sx={{
        minHeight: "100vh", bgcolor: COLORS.background, color: COLORS.ink,
        px: { xs: 2, sm: `${PAGE_PADDING}px` }, py: { xs: 3, sm: `${PAGE_PADDING}px` },
      }}>
        <Box sx={{ width: "100%", maxWidth, mx: "auto" }}>
          <Brand subtitle={subtitle} />
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

// White card section.
export function PublicCard({ children, sx }) {
  return (
    <Box sx={{
      bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.lg}px`,
      boxShadow: SHADOW.card, p: { xs: 2, sm: 3 }, mb: 2, ...sx,
    }}>
      {children}
    </Box>
  );
}

// Page title + muted subtitle (Syne only for the title).
export function PublicTitle({ title, subtitle }) {
  return (
    <Box mb={2}>
      <Typography component="h1" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: { xs: TYPE.section, sm: TYPE.page }, color: COLORS.primaryDeep, lineHeight: 1.25, wordBreak: "break-word" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted, mt: 0.5 }}>{subtitle}</Typography>
      )}
    </Box>
  );
}

// Centred status card for loading / invalid-link / success screens.
export function PublicStatus({ icon, tone = "primary", title, message, loading, action }) {
  const tones = {
    primary: [COLORS.accentSoft, COLORS.primary],
    success: [COLORS.successSoft, COLORS.success],
    error:   [COLORS.errorSoft, COLORS.error],
    muted:   [COLORS.surfaceTint, COLORS.muted],
  };
  const [bg, fg] = tones[tone] || tones.primary;
  return (
    <PublicCard sx={{ textAlign: "center", py: { xs: 4, sm: 6 } }}>
      {loading ? (
        <CircularProgress size={32} sx={{ color: COLORS.primary }} />
      ) : icon && (
        <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: bg, color: fg, mx: "auto",
          display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 32 } }}>
          {icon}
        </Box>
      )}
      {title && (
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, color: COLORS.primaryDeep, mt: 2 }}>
          {title}
        </Typography>
      )}
      {message && (
        <Typography sx={{ fontSize: TYPE.body, color: COLORS.muted, mt: 1, maxWidth: 440, mx: "auto" }}>
          {message}
        </Typography>
      )}
      {action && (
        <Button variant="outlined" onClick={action.onClick} sx={{ mt: 3, height: 36 }}>{action.label}</Button>
      )}
    </PublicCard>
  );
}
